/**
 * Uprawnienia ról po stronie frontu — co pokazać, a co ukryć.
 *
 * Lustro mapy z API (`apps/api/src/shared/permissions.ts`). Rozstrzyga
 * zawsze serwer: trasa sekcji bez uprawnienia odpowiada 403. Front tylko nie
 * pokazuje sekcji, z której i tak nic by nie przyszło — decyzja z 01.10.2026:
 * funkcje bez uprawnień są UKRYTE, nie wyszarzone.
 *
 * Monorepo nie ma wspólnego pakietu, więc mapy nie da się zaimportować z API.
 * Rozjazdu pilnuje test (`permissions.test.ts`), który czyta plik backendu
 * i porównuje rolę po roli — ten sam wzorzec co przy stanach planowania.
 *
 * Klucze uprawnień to nazwy stałych z `PERMISSIONS` w API (np. `FTL_PLANNING`),
 * nie ich polskie opisy — opis jest dla operatora i może się zmienić.
 *
 * Plik bez react-admina (czysta reguła — czyta ją też układ menu); hak
 * i komponent do owijania sekcji są w `PermissionGate.tsx`.
 *
 * ## Centrum ról i uprawnień (22.10.2026)
 *
 * **ZMIANA WYMAGANIA 22.10.2026** (decyzja użytkownika: „centrum zarządzania
 * rolami i uprawnieniami ról […] kto co widzi na froncie, dostęp tylko
 * admin”; przełącznik `roleCenter`, S-22.10-01): uprawnienia ról zmienia
 * administrator w centrum, a API trzyma je w bazie. Front po zalogowaniu
 * pobiera uprawnienia i ukryte elementy swojej roli (`GET /role-center/me`,
 * `roleCenter/roleAccessStore.ts`) i to one rozstrzygają — także dla ról
 * własnych, których lustro nie zna. Lustro niżej zostaje **stanem
 * zapasowym**: przełącznik wyłączony, odczyt się nie udał, rola inna niż
 * zalogowana. Test lustra (`permissions.test.ts`) porównuje je z mapą w kodzie
 * API — to ta sama mapa, z której migracja zasiała bazę centrum, więc
 * z danymi startowymi oba źródła mówią to samo.
 */

import { roleAccessFor } from './roleCenter/roleAccessStore';
import { getFlagsSnapshot } from './featureFlags/featureFlagsApi';

export type PermissionKey =
  | 'ADMIN_SECTION'
  | 'MANAGE_USERS'
  | 'MANAGE_COMPANIES'
  | 'GLOBAL_CONTEXT'
  /** Podpowiedzi pojazdów, warianty, alerty zmian z TMS, flota. */
  | 'FTL_PLANNING'
  /**
   * Obsadzenie zlecenia pojazdem: „Obsadź” z rekomendacji i zmiana pojazdu (01.10.2026);
   * od 12.10.2026 także alert o przeplanowaniu (decyzja użytkownika: widzi tylko master).
   */
  | 'FTL_ASSIGN'
  /** Przewoźnicy z historią, zapytania do przewoźników, giełdy, kartoteka przewoźników. */
  | 'TRADE'
  /** Zapisywanie zmian — bez niego konto tylko ogląda (gość, 01.10.2026). */
  | 'CHANGE_DATA'
  /** Przyjęcie zleceń: prezlecenia, zapis do VIA, skrzynka, komunikaty VIA (CS i Administracja). */
  | 'ORDER_INTAKE'
  /**
   * Obsadzenie mimo blokady z uprawnień pojazdu albo kierowcy — Team Leader
   * (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 54e). Team Leader =
   * planista główny FTL — decyzja użytkownika 19.10.2026; administratorzy
   * też — decyzja użytkownika 20.10.2026 („admin ma wszystkie możliwości”).
   */
  | 'ASSIGN_DESPITE_BLOCK'
  /**
   * Działy przyjęcia zleceń (makieta back office v2, fala B, 21.10.2026;
   * przełącznik `intakeDepartments`): CS — prezlecenia i „Uwagi od FTL”,
   * Administracja — kontrola i zapis do VIA. Przy wyłączonym przełączniku
   * nie są sprawdzane (wystarcza `ORDER_INTAKE`) — patrz `intakeSides.ts`.
   */
  | 'INTAKE_CS'
  | 'INTAKE_ADMINISTRATION'
  /**
   * Akceptacja wyjątku od bramki publikacji (makieta back office v2, fala B,
   * decyzja D9 użytkownika 20.10.2026): rolą — administratorzy, poza tym
   * imiennie na koncie (`permissionGrants`, przełącznik
   * `publicationExceptionRequests`).
   */
  | 'PUBLICATION_EXCEPTION_APPROVE'
  /**
   * Zgody Team Leaderów działów (decyzja użytkownika 24.10.2026): zgoda na
   * zmianę danych zlecenia po wyjeździe na załadunek, gdy pole należy do
   * działu (przełącznik `orderChangeLeadAfterDeparture`, S-25.10-03). Team
   * Leader FTL to planista główny — jego zgodę niesie `FTL_ASSIGN`.
   */
  | 'CS_LEAD_APPROVAL'
  | 'ADMINISTRATION_LEAD_APPROVAL'
  | 'TRADE_LEAD_APPROVAL';

/**
 * Rola → uprawnienia. Kolejność bez znaczenia; zestaw jak w API. `FTL_ASSIGN`
 * (finalne obsadzenie) ma prowadzący podział i administracja, planista FTL
 * i — od 20.10.2026 — rola „Użytkownik” nie; podstawa w komentarzu przy
 * mapie w API.
 */
export const ROLE_PERMISSIONS: Readonly<Record<string, readonly PermissionKey[]>> = {
  ADMIN: [
    'ADMIN_SECTION',
    'MANAGE_COMPANIES',
    'MANAGE_USERS',
    'GLOBAL_CONTEXT',
    'FTL_PLANNING',
    'FTL_ASSIGN',
    'TRADE',
    'CHANGE_DATA',
    'ORDER_INTAKE',
    'ASSIGN_DESPITE_BLOCK',
    'INTAKE_CS',
    'INTAKE_ADMINISTRATION',
    'PUBLICATION_EXCEPTION_APPROVE',
    'CS_LEAD_APPROVAL',
    'ADMINISTRATION_LEAD_APPROVAL',
    'TRADE_LEAD_APPROVAL',
  ],
  COMPANY_ADMIN: [
    'ADMIN_SECTION',
    'MANAGE_USERS',
    'FTL_PLANNING',
    'FTL_ASSIGN',
    'TRADE',
    'CHANGE_DATA',
    'ORDER_INTAKE',
    'ASSIGN_DESPITE_BLOCK',
    'INTAKE_CS',
    'INTAKE_ADMINISTRATION',
    'PUBLICATION_EXCEPTION_APPROVE',
    'CS_LEAD_APPROVAL',
    'ADMINISTRATION_LEAD_APPROVAL',
    'TRADE_LEAD_APPROVAL',
  ],
  FTL_MASTER: ['FTL_PLANNING', 'FTL_ASSIGN', 'TRADE', 'CHANGE_DATA', 'ASSIGN_DESPITE_BLOCK'],
  PLANNER_FTL: ['FTL_PLANNING', 'CHANGE_DATA'],
  TRADER: ['TRADE', 'CHANGE_DATA'],
  // Działy przyjęcia zleceń (fala B, 21.10.2026) — tylko przyjęcie zleceń.
  // Zgody lidera działu mają sami — CS i Administracja nie mają liderów
  // (decyzja użytkownika 26.10.2026); role liderów zostają.
  CS: ['CHANGE_DATA', 'ORDER_INTAKE', 'INTAKE_CS', 'CS_LEAD_APPROVAL'],
  ADMINISTRATION: ['CHANGE_DATA', 'ORDER_INTAKE', 'INTAKE_ADMINISTRATION', 'ADMINISTRATION_LEAD_APPROVAL'],
  // Team Leaderzy działów (24.10.2026): uprawnienia działu + zgody działu.
  CS_LEAD: ['CHANGE_DATA', 'ORDER_INTAKE', 'INTAKE_CS', 'CS_LEAD_APPROVAL'],
  ADMINISTRATION_LEAD: [
    'CHANGE_DATA',
    'ORDER_INTAKE',
    'INTAKE_ADMINISTRATION',
    'ADMINISTRATION_LEAD_APPROVAL',
  ],
  TRADE_LEAD: ['TRADE', 'CHANGE_DATA', 'TRADE_LEAD_APPROVAL'],
  // Bez `FTL_ASSIGN` od 20.10.2026 — obsadzają Team Leader i administratorzy.
  // Oba działy przyjęcia zleceń (fala B) — dzisiejsze konta nic nie tracą.
  USER: [
    'FTL_PLANNING',
    'TRADE',
    'CHANGE_DATA',
    'ORDER_INTAKE',
    'INTAKE_CS',
    'INTAKE_ADMINISTRATION',
  ],
  GUEST: [],
  API_CLIENT: [],
};

/**
 * Czy lustro w kodzie zna rolę (role systemowe). Rola własna z centrum ról
 * (22.10.2026) — nie: jej uprawnienia są tylko w API.
 */
export function mirrorKnowsRole(role: string | null | undefined): boolean {
  return !!role && Object.prototype.hasOwnProperty.call(ROLE_PERMISSIONS, role);
}

/**
 * Czy rola niesie uprawnienie. Brak roli (sesja jeszcze się rozstrzyga)
 * i rola nieznana — nie: lepiej na chwilę ukryć sekcję, niż pokazać ją
 * komuś, komu serwer i tak odmówi.
 */
export function hasPermission(
  role: string | null | undefined,
  permission: PermissionKey,
): boolean {
  // Centrum ról (22.10.2026): stan z API dla zalogowanej roli.
  const access = roleAccessFor(role);
  if (access) return role === 'ADMIN' || access.permissions.includes(permission);
  if (!mirrorKnowsRole(role)) return false;
  if (ROLE_PERMISSIONS[role as string].includes(permission)) return true;
  return keptWithoutFlag(role as string, permission);
}

/**
 * Uprawnienia zdjęte z roli zmianą funkcji schowaną za przełącznikiem przy
 * wydaniu 26.10.2026 — lustro `PERMISSIONS_KEPT_WITHOUT_FLAG` z API. Lustro
 * wyżej to wersja „nowa”; przy wyłączonym przełączniku rola ma uprawnienie jak
 * na produkcji 0.31.7 (`USER` obsadza — `assignTeamLeaderOnly`, S-26.10-01).
 * Dopóki przełączniki się nie wczytały — bez dodatku (ekrany i tak czekają).
 */
export const PERMISSIONS_KEPT_WITHOUT_FLAG: ReadonlyArray<{
  role: string;
  permission: PermissionKey;
  flag: string;
}> = [{ role: 'USER', permission: 'FTL_ASSIGN', flag: 'assignTeamLeaderOnly' }];

function keptWithoutFlag(role: string, permission: PermissionKey): boolean {
  const flags = getFlagsSnapshot().state?.flags;
  if (!flags) return false;
  return PERMISSIONS_KEPT_WITHOUT_FLAG.some(
    (k) => k.role === role && k.permission === permission && flags[k.flag] !== true,
  );
}

/**
 * Czy element interfejsu jest ukryty dla roli w centrum ról (22.10.2026;
 * klucze w `roleCenter/elementKeys.ts`, katalog w API
 * `role-center/ui-elements.ts`). Bez stanu centrum — nic nie jest ukryte
 * ponad uprawnienia. Administrator widzi wszystko.
 */
export function isElementHidden(role: string | null | undefined, element: string): boolean {
  if (role === 'ADMIN') return false;
  const access = roleAccessFor(role);
  return access ? access.hiddenElements.includes(element) : false;
}

/**
 * Czy rola niesie KTÓREKOLWIEK z uprawnień — jak `@RequiresAnyPermission`
 * w API. Pierwsze użycie: status publikacji na karcie zlecenia widzą
 * planowanie FTL i Trade (rozdział FTL §5, 01.10.2026).
 */
export function hasAnyPermission(
  role: string | null | undefined,
  permissions: readonly PermissionKey[],
): boolean {
  return permissions.some((p) => hasPermission(role, p));
}
